import { useEffect, useState } from "react";
import axios from "axios";
import Seo from "@/components/site/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function centsToDollars(cents) {
  if (cents == null || isNaN(cents)) return "";
  const abs = Math.abs(parseInt(cents, 10));
  const whole = Math.floor(abs / 100);
  const fract = abs % 100;
  return `${whole}.${String(fract).padStart(2, "0")}`;
}

function formatDenverDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return iso;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(dt);
}

export default function Tickets() {
  const [state, setState] = useState({ loading: true, event: null, error: null });
  const [canceled, setCanceled] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "cancel") setCanceled(true);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/event`);
        if (!alive) return;
        setState({ loading: false, event: data?.event ?? null, error: null });
      } catch (e) {
        if (!alive) return;
        setState({
          loading: false,
          event: null,
          error: "We couldn't load tickets right now. Please try again shortly.",
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <>
      <Seo
        title="Tickets — The Next Stage"
        description="Tickets for the next live public-speaking competition produced by Use What You Got Publishing in Colorado Springs."
        path="/tickets"
      />
      <section
        data-testid="tickets-page"
        className="max-w-5xl mx-auto px-6 py-16 text-white"
      >
        <header className="border-b border-white/10 pb-8 mb-10">
          <div className="text-xs tracking-[0.25em] opacity-60 mb-3">
            THE NEXT STAGE
          </div>
          <h1
            data-testid="tickets-heading"
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold"
          >
            Tickets
          </h1>
          {canceled && (
            <div
              data-testid="tickets-canceled-banner"
              className="mt-6 text-sm opacity-80 border border-white/15 px-4 py-3"
            >
              Checkout canceled — no charge was made. Pick up where you left off below
              whenever you're ready.
            </div>
          )}
        </header>

        {state.loading && <div data-testid="tickets-loading">Loading…</div>}

        {!state.loading && state.error && (
          <div data-testid="tickets-error" className="text-red-400">
            {state.error}
          </div>
        )}

        {!state.loading && !state.error && !state.event && (
          <div
            data-testid="tickets-empty-state"
            className="text-lg opacity-80 leading-relaxed"
          >
            No upcoming events — check back soon.
          </div>
        )}

        {!state.loading && state.event && <EventBlock event={state.event} />}
      </section>
    </>
  );
}

function EventBlock({ event }) {
  const purchasableTiers = (event.tiers || []).filter((t) => !t.sold_out);
  const [selectedTierId, setSelectedTierId] = useState(
    purchasableTiers[0]?.id || ""
  );
  const [quantity, setQuantity] = useState(1);
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const selectedTier =
    (event.tiers || []).find((t) => t.id === selectedTierId) || null;
  const maxQty = selectedTier
    ? Math.max(1, Math.min(10, selectedTier.remaining || 1))
    : 1;

  const handleCheckout = async (e) => {
    e.preventDefault();
    setErr("");
    if (!selectedTier) {
      setErr("Please pick a ticket.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/public/checkout`,
        {
          tier_id: selectedTier.id,
          quantity: parseInt(quantity, 10),
          buyer_name: buyerName.trim(),
          buyer_email: buyerEmail.trim().toLowerCase(),
        },
        { headers: { Origin: window.location.origin } }
      );
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setErr("Could not start checkout.");
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(
        typeof detail === "string"
          ? detail
          : "Could not start checkout. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="tickets-event">
      <h2
        data-testid="tickets-event-name"
        className="text-2xl sm:text-3xl font-semibold mb-3"
      >
        {event.name}
      </h2>
      <div className="opacity-80 mb-1" data-testid="tickets-event-date">
        {formatDenverDate(event.event_date)}{" "}
        <span className="opacity-60">(Mountain Time)</span>
      </div>
      <div className="opacity-80 mb-10" data-testid="tickets-event-venue">
        {event.venue}
      </div>

      {(!event.tiers || event.tiers.length === 0) ? (
        <div data-testid="tickets-no-tiers" className="opacity-80">
          Ticket sales for this event aren't open yet. Check back soon.
        </div>
      ) : (
        <>
          <ul
            data-testid="tickets-tier-list"
            className="grid sm:grid-cols-2 gap-4 mb-10"
          >
            {event.tiers.map((t) => (
              <TierCard
                key={t.id}
                tier={t}
                selected={selectedTierId === t.id}
                onSelect={() =>
                  !t.sold_out && setSelectedTierId(t.id)
                }
              />
            ))}
          </ul>

          {purchasableTiers.length > 0 && (
            <form
              data-testid="tickets-checkout-form"
              onSubmit={handleCheckout}
              className="border border-white/15 p-6 grid gap-4"
            >
              <h3 className="text-lg font-semibold">Complete your purchase</h3>
              <label className="grid gap-1 text-xs uppercase tracking-wider opacity-80">
                Selected ticket
                <select
                  data-testid="checkout-tier-select"
                  value={selectedTierId}
                  onChange={(e) => setSelectedTierId(e.target.value)}
                  className="bg-black/30 border border-white/20 px-3 py-2 text-sm normal-case tracking-normal"
                >
                  {purchasableTiers.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} — ${centsToDollars(t.price_cents)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="grid gap-1 text-xs uppercase tracking-wider opacity-80">
                Quantity
                <input
                  data-testid="checkout-quantity"
                  type="number"
                  min={1}
                  max={maxQty}
                  value={quantity}
                  onChange={(e) =>
                    setQuantity(
                      Math.max(1, Math.min(maxQty, parseInt(e.target.value || "1", 10)))
                    )
                  }
                  className="bg-black/30 border border-white/20 px-3 py-2 text-sm normal-case tracking-normal w-24"
                />
              </label>

              <label className="grid gap-1 text-xs uppercase tracking-wider opacity-80">
                Your name
                <input
                  data-testid="checkout-name"
                  required
                  value={buyerName}
                  onChange={(e) => setBuyerName(e.target.value)}
                  className="bg-black/30 border border-white/20 px-3 py-2 text-sm normal-case tracking-normal"
                />
              </label>

              <label className="grid gap-1 text-xs uppercase tracking-wider opacity-80">
                Email
                <input
                  data-testid="checkout-email"
                  required
                  type="email"
                  value={buyerEmail}
                  onChange={(e) => setBuyerEmail(e.target.value)}
                  className="bg-black/30 border border-white/20 px-3 py-2 text-sm normal-case tracking-normal"
                />
              </label>

              {selectedTier && (
                <div
                  data-testid="checkout-summary"
                  className="text-sm opacity-90 border-t border-white/10 pt-3"
                >
                  Total:{" "}
                  <span className="tabular-nums font-semibold" data-testid="checkout-total">
                    ${centsToDollars(selectedTier.price_cents * quantity)}
                  </span>{" "}
                  <span className="opacity-60">— charged in Stripe (TEST mode)</span>
                </div>
              )}

              {err && (
                <div data-testid="checkout-error" className="text-red-400 text-sm">
                  {err}
                </div>
              )}

              <button
                data-testid="checkout-submit"
                type="submit"
                disabled={busy || !selectedTier}
                className="mt-2 bg-amber-400 text-slate-900 font-semibold tracking-wide px-6 py-3 hover:bg-amber-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? "Redirecting to Stripe…" : "Buy tickets"}
              </button>
              <p className="text-[11px] opacity-60 tracking-wider uppercase">
                Test mode — use Stripe test card 4242 4242 4242 4242
              </p>
            </form>
          )}
        </>
      )}
    </div>
  );
}

function TierCard({ tier, selected, onSelect }) {
  const soldOut = !!tier.sold_out;
  return (
    <li
      data-testid={`tickets-tier-${tier.id}`}
      aria-disabled={soldOut ? "true" : undefined}
      onClick={onSelect}
      className={[
        "border p-5 flex flex-col gap-2 cursor-pointer transition-colors",
        soldOut
          ? "border-white/10 opacity-40 grayscale cursor-not-allowed"
          : selected
          ? "border-amber-400 bg-amber-400/5"
          : "border-white/20 hover:border-white/40",
      ].join(" ")}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span
          data-testid={`tickets-tier-name-${tier.id}`}
          className="text-lg font-medium"
        >
          {tier.name}
        </span>
        <span
          data-testid={`tickets-tier-price-${tier.id}`}
          className="text-xl tabular-nums"
        >
          ${centsToDollars(tier.price_cents)}
        </span>
      </div>
      <div className="text-xs tracking-wider uppercase opacity-70">
        {soldOut ? (
          <span data-testid={`tickets-tier-soldout-${tier.id}`} className="text-red-400">
            Sold Out
          </span>
        ) : (
          <span data-testid={`tickets-tier-remaining-${tier.id}`}>
            {tier.remaining} remaining
          </span>
        )}
      </div>
    </li>
  );
}
